'use client'

import BookingDetailsIcon from '@/assets/icons/booking-details.svg'
import SettingsIcon from '@/assets/icons/settings.svg'
import TransferIcon from '@/assets/icons/transfer.svg'
import WalletIcon from '@/assets/icons/wallet.svg'
import { PROVIDER_ROUTES } from '@/core/constants/routes'
import { Stepper } from '@/core/ui/components/stepper'
import { usePathname } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'

const useTripDetails = (isUpdate?: boolean) => {
  const pathname = usePathname()
  const segments = pathname.split('/').slice(2)
  let paymentStatus: 'pending' | 'failed' | 'success' = 'pending'
  let step = 0

  if (segments.length === 1) {
    return { step, payment: paymentStatus, tripId: null }
  }

  const [tripId, , path] =
    segments.length === 3 ? segments : [segments[0], '', segments[1]]

  const routes = isUpdate
    ? {
        pay: PROVIDER_ROUTES.updateTripPay(tripId),
        failed: PROVIDER_ROUTES.updateTripPaymentFailed(tripId),
        success: PROVIDER_ROUTES.updateTripPaymentSucceed(tripId),
      }
    : {
        pay: PROVIDER_ROUTES.reservationPay(tripId),
        failed: PROVIDER_ROUTES.reservationPaymentFailed(tripId),
        success: PROVIDER_ROUTES.reservationPaymentSucceed(tripId),
      }

  if (routes.pay.split('/').some(v => v === path)) {
    step = 1
  }
  if (routes.failed.split('/').some(v => v === path)) {
    paymentStatus = 'failed'
    step = 2
  }
  if (routes.success.split('/').some(v => v === path)) {
    paymentStatus = 'success'
    step = 2
  }

  return {
    step,
    payment: paymentStatus,
    tripId,
  }
}

type Props = {
  isUpdate?: boolean
}

export const ProviderTripStepper: React.FC<Props> = ({ isUpdate }) => {
  const t = useTranslations()
  const { payment, step, tripId } = useTripDetails(isUpdate)

  const routes =
    isUpdate && tripId
      ? {
          prepare: PROVIDER_ROUTES.updateTrip(tripId),
          details: PROVIDER_ROUTES.updateTripBookingDetails(tripId),
          pay: PROVIDER_ROUTES.updateTripPay(tripId),
          failed: PROVIDER_ROUTES.updateTripPaymentFailed(tripId),
          success: PROVIDER_ROUTES.updateTripPaymentSucceed(tripId),
        }
      : {
          prepare: PROVIDER_ROUTES.reservation,
          details: PROVIDER_ROUTES.reservationBookingDetails,
          pay: tripId ? PROVIDER_ROUTES.reservationPay(tripId) : '',
          failed: tripId
            ? PROVIDER_ROUTES.reservationPaymentFailed(tripId)
            : '',
          success: tripId
            ? PROVIDER_ROUTES.reservationPaymentSucceed(tripId)
            : '',
        }

  const steps = [
    {
      label: t('trip.prepareTitle'),
      icon: <SettingsIcon />,
      link: routes.prepare,
    },
    {
      label: t('trip.detailsTitle'),
      icon: <BookingDetailsIcon />,
      link: routes.details,
    },
    {
      label: t('trip.transferTitle'),
      icon: <TransferIcon />,
      link: routes.pay,
    },
    {
      label: t('trip.paymentTitle'),
      icon:
        payment === 'failed' ? (
          <div className='border-red bg-red z-10 flex h-full w-full items-center justify-center rounded-full border p-5 text-2xl text-white'>
            &#x2715;
          </div>
        ) : (
          <WalletIcon />
        ),
      link: payment === 'failed' ? routes.failed : routes.success,
    },
  ]

  return <Stepper activeStep={step} steps={steps} canBack={step < 2} />
}
